import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { CoachingAgentPicker } from '../../components/CoachingAgentPicker';
import { Button } from '../../../web/components-v2/Button';
import { Toggle } from '../../../web/components/Toggle';

/**
 * Drawer-tab settings surface for the focused-view coaching pipeline. Mirrors the standard queue's
 * "Coaching Settings" panel, down to sharing its `CoachingAgentPicker`.
 *
 * The bottom "Use focused view" toggle is preserved from that panel so a rep can switch back to the
 * standard queue (turning it off persists the opt-out and navigates to `/queue`); the "Return to
 * standard queue" link mirrors the same action for discoverability.
 *
 * Presentation only, apart from the agent picker, which owns its own paging and search.
 */

export type SettingsPanelProps = {
	realtimeCoachingEnabled: boolean;
	autoSuggestionsEnabled: boolean;
	selectedCoachingTemplateId: string | null;
	/** Whether the rep is currently opted into focused view (the "Use focused view" toggle state). */
	focusedViewEnabled: boolean;
	onRealtimeChange: (enabled: boolean) => void;
	onAutoSuggestionsChange: (enabled: boolean) => void;
	onTemplateChange: (id: string | null) => void;
	onReturnToQueue: () => void;
};

export const SettingsPanel: FC<SettingsPanelProps> = ({
	realtimeCoachingEnabled,
	autoSuggestionsEnabled,
	selectedCoachingTemplateId,
	focusedViewEnabled,
	onRealtimeChange,
	onAutoSuggestionsChange,
	onTemplateChange,
	onReturnToQueue,
}) => {
	return (
		<div className={css(styles.root)}>
			<div className={css(styles.section)}>
				<h3 className={css(styles.title)}>Realtime Coaching</h3>
				<Toggle
					id='realtime-coaching-toggle'
					isOn={realtimeCoachingEnabled}
					text='Enable AI coaching during calls'
					onToggleCheckChanged={onRealtimeChange}
				/>
			</div>

			{realtimeCoachingEnabled && (
				<div className={css(styles.sectionDivider)}>
					<h3 className={css(styles.title)}>Auto Suggestions</h3>
					<Toggle
						id='auto-suggestions-toggle'
						isOn={autoSuggestionsEnabled}
						text='Automatically suggest during calls'
						onToggleCheckChanged={onAutoSuggestionsChange}
					/>
				</div>
			)}

			{realtimeCoachingEnabled && (
				<div className={css(styles.sectionDivider)}>
					<h3 className={css(styles.title)}>Choose AI Agent</h3>
					<CoachingAgentPicker selectedTemplateId={selectedCoachingTemplateId} onChange={onTemplateChange} />
				</div>
			)}

			<div className={css(styles.sectionDivider)}>
				<h3 className={css(styles.title)}>Focused View</h3>
				{/*
				 * Only rendered while focused view is on (always true for this panel,
				 * which lives inside the focused view). Gating the render avoids a dead
				 * "off" toggle with no way to turn focused view back on from here — the
				 * "Return to standard queue" link below is the unambiguous off action.
				 */}
				{focusedViewEnabled && (
					<Toggle
						id='focused-view-toggle'
						isOn={true}
						text='Use the new focused lead view'
						onToggleCheckChanged={next => {
							if (!next) onReturnToQueue();
						}}
					/>
				)}
				<Button variant='link' size='sm' styles={[styles.returnLink]} onClick={onReturnToQueue}>
					← Return to standard queue
				</Button>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		// `--bg-elev` matches sibling drawer tabs (PinConfigPanel, Wrap-up) so all
		// tabs in the SideDrawer read as the same elevation surface.
		background: 'var(--bg-elev)',
		padding: 20,
		color: 'var(--ink)',
		overflowY: 'auto',
	},
	section: {
		paddingBottom: 4,
	},
	sectionDivider: {
		marginTop: 16,
		paddingTop: 16,
		borderTop: '1px solid var(--border)',
	},
	title: {
		fontSize: 14,
		fontWeight: 600,
		marginBottom: 10,
		marginTop: 0,
		color: 'var(--ink)',
	},
	returnLink: {
		padding: 0,
		marginTop: 12,
		justifyContent: 'flex-start',
	},
});
